/**
 * ReadAloudMiniPlayer — the reader's hand on a readout in progress.
 *
 * Miles, 2026-09-10: "the user should be able quickly turn on the read out loud
 * functionality and when the user continues to hover while playing it needs to
 * allow the user skip between summarized sections moving back and fourth, pause
 * or play or restart, slow down or speed up audio in increments of 10% points at
 * a time, all in an extremely great user experience"; and, on the speed control,
 * "it should be a dynamic scroll from left to right in 10% increments not 50%".
 *
 * Six controls, in the order a hand reaches for them: start over · back a
 * section · pause/play · forward a section · slower · the slider · faster, with
 * "2 / 5 · 120 %" saying where the readout is and how fast it is going.
 *
 * IT IS SHOWN WHENEVER THIS ROW IS THE ONE BEING READ — getting ready, playing,
 * or paused — never gated on hover: a control that vanishes when the pointer
 * drifts is the opposite of what was asked for. Every other row's meta line is
 * untouched, so a long transcript gains nothing at rest.
 *
 * Chrome only (D9): every string here is a label, and each button is a straight
 * call into the player's own command surface — nothing here decides anything.
 */

import React from 'react';
import {
  ReadAloudControlButton,
  ReadAloudControls,
  ReadAloudMeter,
  ReadAloudSpeedSlider,
} from './styles';
import { readAloudPlayerCopy } from '../../services/readAloud/readAloudCopy';
import {
  READ_ALOUD_MAX_RATE,
  READ_ALOUD_MIN_RATE,
  READ_ALOUD_RATE_STEP,
  restartReadAloud,
  seekReadAloudSegment,
  setReadAloudRate,
  stepReadAloudRate,
  toggleReadAloudPause,
  type ReadAloudState,
} from '../../services/readAloud/player';

const PrevIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6 9 12l9 6z" />
    <line x1="6" y1="6" x2="6" y2="18" />
  </svg>
);

const NextIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 6l9 6-9 6z" />
    <line x1="18" y1="6" x2="18" y2="18" />
  </svg>
);

const PauseIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="9" y1="5" x2="9" y2="19" />
    <line x1="15" y1="5" x2="15" y2="19" />
  </svg>
);

const PlayIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M7 5l12 7-12 7z" />
  </svg>
);

const RestartIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 12a9 9 0 1 0 3-6.7" />
    <polyline points="3 4 3 9 8 9" />
  </svg>
);

const MinusIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="6" y1="12" x2="18" y2="12" />
  </svg>
);

const PlusIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="6" y1="12" x2="18" y2="12" />
    <line x1="12" y1="6" x2="12" y2="18" />
  </svg>
);

export interface ReadAloudMiniPlayerProps {
  state: ReadAloudState;
  /** 0-based, as the player publishes it; the label counts from 1. */
  segment: number;
  segmentCount: number;
  rate: number;
  /** The message's own language, so a Spanish message's controls read in Spanish. */
  language?: string;
}

/** A percentage the slider can carry, in whole ten-point steps. */
const PERCENT_MIN = Math.round(READ_ALOUD_MIN_RATE * 100);
const PERCENT_MAX = Math.round(READ_ALOUD_MAX_RATE * 100);
const PERCENT_STEP = Math.round(READ_ALOUD_RATE_STEP * 100);

export const ReadAloudMiniPlayer: React.FC<ReadAloudMiniPlayerProps> = ({
  state,
  segment,
  segmentCount,
  rate,
  language,
}) => {
  if (state === 'idle') return null;
  const copy = readAloudPlayerCopy(language);
  const total = Math.max(1, segmentCount);
  const current = Math.min(total, Math.max(1, segment + 1));
  const paused = state === 'paused';
  // While the opening segment is still rendering there is nothing to skip
  // through yet, so the transport keeps its shape and simply waits.
  const busy = state === 'loading';
  const atStart = busy || current <= 1;
  const atEnd = busy || current >= total;
  const percent = Math.round(rate * 100);

  return (
    <ReadAloudControls data-read-aloud-player="" role="group" aria-label={copy.player}>
      <ReadAloudControlButton
        type="button"
        data-read-aloud-restart=""
        aria-label={copy.restart}
        title={copy.restart}
        disabled={busy}
        onClick={() => restartReadAloud()}
      >
        <RestartIcon />
      </ReadAloudControlButton>
      <ReadAloudControlButton
        type="button"
        data-read-aloud-prev=""
        aria-label={copy.previous}
        title={copy.previous}
        disabled={atStart}
        onClick={() => seekReadAloudSegment(-1)}
      >
        <PrevIcon />
      </ReadAloudControlButton>
      <ReadAloudControlButton
        type="button"
        data-read-aloud-playpause=""
        data-read-aloud-paused={paused ? 'true' : 'false'}
        aria-label={paused ? copy.play : copy.pause}
        title={paused ? copy.play : copy.pause}
        disabled={busy}
        onClick={() => toggleReadAloudPause()}
      >
        {paused ? <PlayIcon /> : <PauseIcon />}
      </ReadAloudControlButton>
      <ReadAloudControlButton
        type="button"
        data-read-aloud-next=""
        aria-label={copy.next}
        title={copy.next}
        disabled={atEnd}
        onClick={() => seekReadAloudSegment(1)}
      >
        <NextIcon />
      </ReadAloudControlButton>
      <ReadAloudControlButton
        type="button"
        data-read-aloud-slower=""
        aria-label={copy.slower}
        title={copy.slower}
        disabled={percent <= PERCENT_MIN}
        onClick={() => stepReadAloudRate(-1)}
      >
        <MinusIcon />
      </ReadAloudControlButton>
      <ReadAloudSpeedSlider
        type="range"
        data-read-aloud-speed=""
        min={PERCENT_MIN}
        max={PERCENT_MAX}
        step={PERCENT_STEP}
        value={percent}
        aria-label={copy.speed}
        title={`${copy.speed} · ${copy.ratePercent(rate)}`}
        onChange={(e) => setReadAloudRate(Number(e.target.value) / 100)}
      />
      <ReadAloudControlButton
        type="button"
        data-read-aloud-faster=""
        aria-label={copy.faster}
        title={copy.faster}
        disabled={percent >= PERCENT_MAX}
        onClick={() => stepReadAloudRate(1)}
      >
        <PlusIcon />
      </ReadAloudControlButton>
      <ReadAloudMeter data-read-aloud-position="">
        {`${copy.position(current, total)} · ${copy.ratePercent(rate)}`}
      </ReadAloudMeter>
    </ReadAloudControls>
  );
};

export default ReadAloudMiniPlayer;
